import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Topbar from '../components/Topbar';
import api from '../api';

const COLORS = ['#066ac9','#0cbc87','#f7c32e','#d6293e','#6f42c1','#fd7e14'];

export default function Dashboard() {
  const navigate = useNavigate();
  const [formulaires, setFormulaires] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qrModal, setQrModal] = useState(null);
  const isAdmin = (() => { try { return !!(JSON.parse(localStorage.getItem('enseignant') || '{}').is_admin); } catch { return false; } })();

  useEffect(() => {
    Promise.all([api.get('/formulaires'), api.get('/classes')]).then(([f, c]) => {
      setFormulaires(f.data);
      setClasses(c.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const totalEleves = classes.reduce((s, c) => s + (c.nb_eleves || 0), 0);
  const totalReponses = formulaires.reduce((s, f) => s + (f.nb_reponses || 0), 0);

  const openQr = async (id) => {
    const { data } = await api.get(`/formulaires/${id}/qrcode`);
    setQrModal(data);
  };

  const deleteFormulaire = async (id) => {
    if (!window.confirm('Supprimer ce formulaire et toutes ses réponses ?')) return;
    await api.delete(`/formulaires/${id}`);
    setFormulaires(f => f.filter(x => x.id !== id));
  };

  const telechargerPdf = async (f) => {
    const { data } = await api.get(`/formulaires/${f.id}/pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${f.titre}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const dupliquerFormulaire = async (id) => {
    const { data } = await api.post(`/formulaires/${id}/dupliquer`);
    const { data: updated } = await api.get('/formulaires');
    setFormulaires(updated);
  };

  const toggleActif = async (f) => {
    await api.put(`/formulaires/${f.id}`, { actif: !f.actif });
    setFormulaires(prev => prev.map(x => x.id === f.id ? { ...x, actif: !x.actif } : x));
  };

  if (loading) return (
    <div className="d-flex align-items-center justify-content-center" style={{ height: '80vh' }}>
      <div className="spinner-border" style={{ color: 'var(--primary)' }}></div>
    </div>
  );

  return (
    <>
      <Topbar
        title="Tableau de bord"
        actions={
          <Link to="/formulaire/creer" className="btn btn-primary">
            <i className="bi bi-plus-lg me-2"></i>Nouveau formulaire
          </Link>
        }
      />
      <div className="page-content">

        {/* Stats */}
        <div className="row g-3 mb-4">
          {[
            { label: 'Formulaires', value: formulaires.length, icon: 'bi-clipboard-check-fill', color: '#e8f0fe', iconColor: '#066ac9' },
            { label: 'Élèves enregistrés', value: totalEleves, icon: 'bi-person-fill', color: '#cef2e7', iconColor: '#0cbc87' },
            { label: 'Réponses totales', value: totalReponses, icon: 'bi-chat-square-text-fill', color: '#fdf3d5', iconColor: '#f7c32e' },
            { label: 'Classes', value: classes.length, icon: 'bi-people-fill', color: '#ede9fe', iconColor: '#6f42c1' },
          ].map(s => (
            <div key={s.label} className="col-6 col-xl-3">
              <div className="stat-card">
                <div className="stat-icon" style={{ background: s.color }}>
                  <i className={`bi ${s.icon}`} style={{ color: s.iconColor }}></i>
                </div>
                <div>
                  <div className="stat-value">{s.value}</div>
                  <div className="stat-label">{s.label}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="row g-4">
          {/* Formulaires */}
          <div className="col-xl-8">
            <div className="card h-100">
              <div className="card-header d-flex justify-content-between align-items-center">
                <span className="fw-semibold">Mes formulaires</span>
                <Link to="/formulaire/creer" className="btn btn-sm btn-primary">
                  <i className="bi bi-plus-lg me-1"></i>Créer
                </Link>
              </div>
              <div className="card-body p-0">
                {formulaires.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <i className="bi bi-clipboard-x" style={{ fontSize: '2.5rem', opacity: 0.3 }}></i>
                    <p className="mt-2 mb-0">Aucun formulaire encore.<br />
                      <Link to="/formulaire/creer">Créez votre premier formulaire →</Link>
                    </p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr>
                          <th className="ps-3">Titre</th>
                          <th>Code</th>
                          <th>Questions</th>
                          <th>Réponses</th>
                          <th>Statut</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formulaires.map(f => (
                          <tr key={f.id}>
                            <td className="ps-3">
                              <Link to={`/formulaire/${f.id}`} className="fw-medium text-decoration-none">
                                {f.titre}
                              </Link>
                            </td>
                            <td>
                              <code className="text-primary" style={{ fontSize: '0.8rem' }}>{f.code_acces}</code>
                            </td>
                            <td><span className="badge bg-light text-dark">{f.nb_questions}</span></td>
                            <td><span className="badge bg-light text-dark">{f.nb_reponses}</span></td>
                            <td>
                              <span className={`badge ${f.actif ? 'bg-success' : 'bg-secondary'}`}>
                                {f.actif ? 'Actif' : 'Fermé'}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex gap-1">
                                <button className="btn btn-sm btn-light" title="QR Code" onClick={() => openQr(f.id)}>
                                  <i className="bi bi-qr-code"></i>
                                </button>
                                <Link to={`/formulaire/${f.id}/modifier`} className="btn btn-sm btn-light" title="Modifier">
                                  <i className="bi bi-pencil"></i>
                                </Link>
                                <button className="btn btn-sm btn-light" title="Dupliquer" onClick={() => dupliquerFormulaire(f.id)}>
                                  <i className="bi bi-copy"></i>
                                </button>
                                <button className="btn btn-sm btn-light" title="Télécharger en PDF" onClick={() => telechargerPdf(f)}>
                                  <i className="bi bi-file-earmark-pdf"></i>
                                </button>
                                <button className="btn btn-sm btn-light" title={f.actif ? 'Fermer' : 'Ouvrir'} onClick={() => toggleActif(f)}>
                                  <i className={`bi ${f.actif ? 'bi-lock' : 'bi-unlock'}`}></i>
                                </button>
                                {isAdmin && (
                                  <button className="btn btn-sm btn-light text-danger" title="Supprimer" onClick={() => deleteFormulaire(f.id)}>
                                    <i className="bi bi-trash"></i>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Classes */}
          <div className="col-xl-4">
            <div className="card h-100">
              <div className="card-header d-flex justify-content-between align-items-center">
                <span className="fw-semibold">Classes</span>
                <Link to="/classes" className="btn btn-sm btn-light">Voir tout</Link>
              </div>
              <div className="card-body p-0">
                {classes.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <i className="bi bi-people" style={{ fontSize: '2rem', opacity: 0.3 }}></i>
                    <p className="mt-2 mb-0 small">Aucune classe</p>
                  </div>
                ) : (
                  <ul className="list-group list-group-flush">
                    {classes.map((c, i) => (
                      <li key={c.id} className="list-group-item d-flex align-items-center justify-content-between px-3 py-2">
                        <div className="d-flex align-items-center gap-2">
                          <div className="avatar-sm" style={{ background: COLORS[i % COLORS.length] }}>
                            {c.nom.charAt(0)}
                          </div>
                          <div>
                            <div className="fw-medium" style={{ fontSize: '0.875rem' }}>{c.nom}</div>
                            {c.niveau && <div className="text-muted" style={{ fontSize: '0.75rem' }}>{c.niveau}</div>}
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <span className="badge bg-light text-dark">{c.nb_eleves} élève{c.nb_eleves !== 1 ? 's' : ''}</span>
                          <Link to={`/classe/${c.id}`} className="btn btn-sm btn-light">
                            <i className="bi bi-chevron-right"></i>
                          </Link>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* QR Code Modal */}
      {qrModal && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={() => setQrModal(null)}>
          <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">QR Code & Lien élève</h5>
                <button className="btn-close" onClick={() => setQrModal(null)}></button>
              </div>
              <div className="modal-body qr-container">
                <img src={qrModal.qr} alt="QR Code" style={{ width: 220, height: 220 }} />
                <p className="mt-3 mb-1 fw-medium">Lien direct :</p>
                <div className="d-flex align-items-center gap-2 justify-content-center">
                  <code className="bg-light px-3 py-1 rounded" style={{ fontSize: '0.9rem' }}>{qrModal.url}</code>
                  <button className="btn btn-sm btn-light" onClick={() => navigator.clipboard.writeText(qrModal.url)}>
                    <i className="bi bi-clipboard"></i>
                  </button>
                </div>
                <p className="mt-2 text-muted" style={{ fontSize: '0.8rem' }}>
                  Code d'accès : <strong>{qrModal.code}</strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
